import { useState, useEffect } from 'react'
import { useAppDispatch, useIsAuthenticated, useUser } from '@/store/hooks'
import { loginUser, clearError } from '@/store/slices/authSlice'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { sessionService } from '@/services/sessionService'
import { useAuthStore } from '@/store/authStore'

const AdminControlPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isAuthenticated = useIsAuthenticated()
  const user = useUser()
  const { login: loginZustand } = useAuthStore()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'universal_admin' || user.role === 'office_admin')) {
      navigate('/app/dashboard', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  // Clear any existing errors on component mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    
    try {
      console.log('Starting admin login...')
      const resultAction = await dispatch(loginUser({ 
        email, 
        password, 
        loginType: 'admin' 
      }))
      
      if (loginUser.fulfilled.match(resultAction)) {
        const { user, sessionId, token, refreshToken } = resultAction.payload
        console.log('Login successful, user:', user)
        
        // Check if user has admin privileges
        if (user && (user.role === 'universal_admin' || user.role === 'office_admin')) {
          console.log('Admin user authenticated, starting session and navigating...')
          
          // Sync Zustand store with Redux state
          try {
            await loginZustand(email, password, 'admin')
          } catch (err) {
            // If Zustand login fails, manually set the state
            useAuthStore.setState({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
          
          // Start session tracking
          if (sessionId) {
            sessionService.startSession(sessionId)
          }
          
          // Wait a brief moment to ensure Redux state is persisted
          // The state should already be set by the fulfilled action
          await new Promise(resolve => setTimeout(resolve, 150))
          
          // Navigate to dashboard - ensure we're going to the correct route
          navigate('/app/dashboard', { replace: true })
        } else {
          console.error('User role not authorized for admin access:', user?.role)
          setError('Access denied. Your account does not have admin privileges.')
          setIsLoading(false)
        }
      } else {
        // Handle login failure
        const errorMessage = resultAction.payload as string
        console.error('Admin login failed:', errorMessage)
        setError(errorMessage || 'Login failed. Please check your credentials.')
        setIsLoading(false)
      }
    } catch (error: any) {
      console.error('Admin login error:', error)
      setError(error.message || 'Login failed. Please check your credentials.')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Admin Control Panel
            </h2>
            <p className="text-gray-600 mb-6">
              New Friends Colony RWA Administration
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Admin Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="admin@nfcrwa.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Access Admin Panel'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <Link 
              to="/" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back to Home
            </Link>
            <div className="text-xs text-gray-500">
              Authorized personnel only
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminControlPage
